import { useReducer } from 'react';

import { CustomThemeDesign, Sku } from 'models/sku.model';

export enum ActionType {
  DisableCardDesignCustomisation,
  EnableCardDesignCustomisation,
  ResetCardDesignCustomisation,
  SelectCardDesign,
  CreateCustomThemeDesign,
  SelectCustomCardDesign,
}

export type Payload = {
  uid?: string;
  imageUrl?: string;
  templateUid?: string;
  customThemeDesign?: CustomThemeDesign;
};

export interface IAction {
  type: ActionType;
  payload?: Payload;
}

export type CustomCardDesign = {
  imageUrl?: string;
  isTemplate: boolean;
  uid: string;
};

export type State = {
  customCardDesignIsEnabled: boolean;
  customThemeDesigns?: CustomThemeDesign[];
  initialCardDesign?: CustomCardDesign;
  selectedCardDesign?: CustomCardDesign;
};

const getInitalState = (sku: Sku): State => {
  const emptyState = {
    customCardDesignIsEnabled: false,
    customThemeDesign: null,
    initialCardDesign: null,
    selectedCardDesign: null,
  };
  if (!sku) return emptyState;

  if (sku.themes?.length) {
    const { uid, image, cmsImage } = sku.themes[0];
    const cardDesign = {
      imageUrl: cmsImage ? cmsImage.url : image,
      isTemplate: false,
      uid,
    };
    return {
      customCardDesignIsEnabled: false,
      customThemeDesigns: [],
      initialCardDesign: cardDesign,
      selectedCardDesign: cardDesign,
    };
  } else if (sku.templates?.length) {
    const { uid, imagePreview, cmsImagePreview } = sku.templates[0];
    const cardDesign = {
      imageUrl: cmsImagePreview ? cmsImagePreview.url : imagePreview,
      isTemplate: true,
      uid,
    };
    return {
      customCardDesignIsEnabled: false,
      customThemeDesigns: [],
      initialCardDesign: cardDesign,
      selectedCardDesign: cardDesign,
    };
  }
  return emptyState;
};

export const reducer = (state: State, action: IAction): State => {
  const { type, payload } = action;

  switch (type) {
    case ActionType.DisableCardDesignCustomisation:
      return { ...state, customCardDesignIsEnabled: false };
    case ActionType.EnableCardDesignCustomisation:
      return { ...state, customCardDesignIsEnabled: true };
    case ActionType.SelectCardDesign: {
      const { uid, imageUrl } = payload;
      return {
        ...state,
        selectedCardDesign: {
          ...state.selectedCardDesign,
          imageUrl,
          isTemplate: false,
          uid,
        },
      };
    }
    case ActionType.SelectCustomCardDesign: {
      const { uid, imageUrl } = payload;
      return {
        ...state,
        selectedCardDesign: {
          ...state.selectedCardDesign,
          isTemplate: true,
          imageUrl,
          uid,
        },
      };
    }
    case ActionType.CreateCustomThemeDesign: {
      const { customThemeDesign, templateUid } = payload;
      return {
        ...state,
        customThemeDesigns: [
          //remove existing custom theme with same template UID
          ...state.customThemeDesigns.filter(t => t.templateUid !== templateUid),
          { ...customThemeDesign, templateUid },
        ],
      };
    }
    case ActionType.ResetCardDesignCustomisation: {
      return {
        customCardDesignIsEnabled: false,
        initialCardDesign: state.initialCardDesign,
        selectedCardDesign: state.initialCardDesign,
      };
    }
    default:
      return state;
  }
};

export const useCardCustomisationReducer = (sku: Sku) => useReducer(reducer, getInitalState(sku));
